import { ForwardedRef, useEffect, useImperativeHandle, useState } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';

import { removeActiveElement, setActiveElement } from '../../lib/dom/selectors';
import { serializeRecord } from '../../lib/records/serialize-record';

interface UseSelectorConfig {
	command: (...args: any[]) => void;
	hash: string;
	items: any[];
	ref: ForwardedRef<any>;
}

export interface SelectorHandle {
	onKeyDown: ({ event }: { event: KeyboardEvent }) => boolean;
}

export const useSelector = ({ command, hash, items, ref }: UseSelectorConfig) => {
	const { track } = useAnalytics();
	const [selectedIndex, setSelectedIndex] = useState(0);

	const selectIndex = (index: number) => {
		setSelectedIndex(index);
		setActiveElement(hash, index);

		// Get the position of the selected item in the parent container and scroll to it if it is not visible
		const selectedElement = document.getElementById(`options-${hash}-${index}`);

		if (selectedElement?.scrollIntoView) {
			selectedElement.scrollIntoView({ block: 'nearest' });
		}
	};

	const selectItem = (index: number) => {
		const item = items[index];

		if (item !== undefined) {
			if (item.type === 'group') {
				track(AnalyticsEvent.GiveBox.GroupSelected, {
					groupType: item.dimension,
					displayName: item.displayName,
					token: item.username,
				});
			}
			command(serializeRecord(item));
		}
	};

	const upHandler = () => {
		selectIndex((selectedIndex + items.length - 1) % items.length);
	};

	const downHandler = () => {
		selectIndex((selectedIndex + 1) % items.length);
	};

	const enterHandler = () => {
		selectItem(selectedIndex);
	};

	useEffect(() => {
		setSelectedIndex(0);

		if (items.length) {
			setActiveElement(hash, 0);
		} else {
			removeActiveElement(hash);
		}
	}, [hash, items]);

	useImperativeHandle(ref, () => ({
		onKeyDown: ({ event }: { event: KeyboardEvent }) => {
			if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
				upHandler();
				return true;
			}

			if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
				downHandler();
				return true;
			}

			if (event.key === 'Enter') {
				enterHandler();
				return true;
			}

			return false;
		},
	}));

	const getOptionProps = (index: number) => ({
		'aria-selected': index === selectedIndex,
		id: `options-${hash}-${index}`,
		onClick: () => selectItem(index),
	});

	const getSelectorProps = () => ({
		id: `selector-${hash}`,
	});

	return {
		getOptionProps,
		getSelectorProps,
		selectedIndex,
		selectItem,
	};
};
